import React, { useEffect, useRef } from 'react';
import Editor from '@toast-ui/editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { uploadFileViaPresignedUrl } from '@/services/fileService';

interface ToastHtmlEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  /** true 이면 에디터 위에 덮개를 씌워 입력을 막는다 (저장 중 등). 에디터는 언마운트하지 않는다. */
  isBusy?: boolean;
  /** isBusy 일 때 덮개에 표시할 문구 */
  busyMessage?: string;
}

/**
 * TOAST UI Editor v3 의 Toolbar 는 크기 변화(ResizeObserver) 시 `handleResize` 를 200ms throttle 로 실행한다.
 * 이 trailing 호출은 `destroy()` 이후에도 남아 있다가 실행되어, 이미 정리된 vdom 을 다시 그리려다
 * `removeChild` NotFoundError 를 낸다. (화면 이동으로 언마운트되면 크기가 바뀌어 거의 항상 호출이 예약됨)
 */
const TOOLBAR_RESIZE_THROTTLE_MS = 200;
/** 대기 중인 throttle 호출이 모두 끝난 뒤 destroy 하기 위한 지연 (throttle 간격 + 여유) */
const DESTROY_DELAY_MS = TOOLBAR_RESIZE_THROTTLE_MS + 100;

/**
 * TOAST UI Editor wrapper (HTML mode output)
 * 언마운트 처리:
 * - mountedRef 를 먼저 false 로 내려 change/value 동기화 콜백이 더 이상 동작하지 않게 한다.
 * - 에디터 root(el)는 즉시 문서에서 분리하고(화면에서는 바로 사라짐), 에디터 내부 DOM 은 건드리지 않는다.
 * - destroy() 는 Toolbar 의 대기 중 throttle 호출이 끝난 뒤(DESTROY_DELAY_MS) 실행한다.
 * 이미지 업로드:
 * - addImageBlobHook을 통해 이미지 파일을 서버에 먼저 업로드 후 URL로 삽입
 */
export default function ToastHtmlEditor({
  value,
  onChange,
  placeholder = '',
  height = '320px',
  isBusy = false,
  busyMessage = '',
}: ToastHtmlEditorProps): React.ReactElement {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const onChangeRef = useRef(onChange);
  const mountedRef = useRef(false);
  onChangeRef.current = onChange;

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const el = document.createElement('div');
    wrapper.appendChild(el);
    mountedRef.current = true;

    const instance = new Editor({
      el,
      height,
      initialEditType: 'wysiwyg',
      previewStyle: 'vertical',
      initialValue: value || '',
      placeholder,
      hideModeSwitch: true,
      usageStatistics: false,
      events: {
        change: () => {
          // 언마운트 후 내부 tick이 호출될 경우 무시
          if (!mountedRef.current) return;
          const html = instance.getHTML();
          const normalized = html === '<p><br></p>' ? '' : html;
          onChangeRef.current(normalized);
        },
      },
      hooks: {
        addImageBlobHook: async (blob, callback) => {
          try {
            // File 객체로 변환 (파일명 생성)
            const file = new File([blob], `image-${Date.now()}.${blob.type.split('/')[1]}`, {
              type: blob.type,
            });
            
            // 파일 업로드 API 호출
            const imageUrl = await uploadFileViaPresignedUrl(file, 'CREATOR');
            
            // 업로드된 URL을 에디터에 삽입
            callback(imageUrl, `Uploaded image`);
          } catch (error) {
            console.error('이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다.');
            callback('', '');
          }
        },
      },
    });

    editorRef.current = instance;

    return () => {
      mountedRef.current = false;
      editorRef.current = null;
      // 화면에서는 즉시 제거. el 은 분리된 상태로도 내부 vdom 이 일관되므로 대기 중인 리렌더가 안전하다.
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      window.setTimeout(() => {
        try {
          instance.destroy();
        } catch {
          // ignore
        }
      }, DESTROY_DELAY_MS);
    };
  // 최초 마운트 시 한 번만 실행
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const instance = editorRef.current;
    if (!instance || !mountedRef.current) return;
    const next = value || '';
    try {
      const current = instance.getHTML();
      const normalizedCurrent = current === '<p><br></p>' ? '' : current;
      if (normalizedCurrent !== next) {
        instance.setHTML(next);
      }
    } catch {
      // ignore
    }
  }, [value]);

  return (
    <div className="relative">
      <div ref={wrapperRef} className="rounded-md border border-gray-300" />
      {isBusy && (
        <div className="absolute inset-0 flex items-center justify-center rounded-md bg-white/70">
          <p className="text-sm text-gray-600">{busyMessage}</p>
        </div>
      )}
    </div>
  );
}

